import * as _ from 'lodash';
import * as moment from 'moment';
import { Component, ViewChild, Input, Output, EventEmitter, ChangeDetectorRef, SimpleChanges } from '@angular/core';
import { UntypedFormGroup, NgForm, AbstractControl, UntypedFormArray, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { formats as formatsCatalog } from 'src/app/utils/catalog';
import helpers from 'src/app/utils/helpers';
import { SubscriptionHandlerComponent } from '../../misc/subscription-handler/subscription-handler.component';

@Component({
  template: '',
})
export class BaseFormComponent extends SubscriptionHandlerComponent {
  @ViewChild('form', { static: true }) public form: NgForm;

  @Input('values')
  set values(values: any) {
    this.inputValues = this.modifyInputValues(_.cloneDeep(values));
    this.inputValuesChange.emit(this.inputValues);
  }

  get values(): any {
    return this.formGroup.getRawValue();
  }

  @Input('withSubmit') public withSubmit: boolean = true;
  @Input('submitText') public submitText: string = 'forms.general.buttons.submit';
  @Input('reset') public reset: boolean = true;
  @Input('readonly') public readonly: Array<string> = [];
  @Input('hidden') public hidden: Array<string> = [];

  @Output('onSubmit') handleSubmit = new EventEmitter<{
    values: any;
    formLoading: FormLoading;
  }>();

  public formLoading: FormLoading = new FormLoading();
  public errors: Array<string>;

  public messages: any;

  public inputValues: any = {};
  public inputValuesChange = new EventEmitter<any>();

  private formArrays: {
    [path: string]: {
      itemConstructor: (values?: any) => AbstractControl;
    };
  } = {};

  private inputValuesModifiers: Array<(values: any) => any> = [];
  private outputValuesModifiers: Array<(values: any) => any> = [];

  constructor(public translator: TranslateService, public changeDetection: ChangeDetectorRef, public formGroup: UntypedFormGroup) {
    super();

    this.messages = {
      email: () => this.translator.instant('forms.general.errors.email'),
      oneChecked: () => this.translator.instant('forms.general.errors.oneChecked'),
      oneFilled: () => this.translator.instant('forms.general.errors.oneFilled'),
      pattern: () => this.translator.instant('forms.general.errors.pattern'),
      required: () => this.translator.instant('forms.general.errors.required'),
      invalidDate: (error: any) =>
        this.translator.instant('forms.general.errors.invalidDate', {
          format: error.format,
        }),
      invalidValue: () => this.translator.instant('forms.general.errors.invalidValue'),
      min: (error: any) =>
        this.translator.instant('forms.general.errors.minNumber', {
          min: error.min,
        }),
      max: (error: any) =>
        this.translator.instant('forms.general.errors.maxNumber', {
          max: error.max,
        }),
      matDatepickerMin: (error: any) =>
        this.translator.instant('forms.general.errors.minDate', {
          min: moment(error.min).format(formatsCatalog.date.moment),
        }),
      matDatepickerMax: (error: any) =>
        this.translator.instant('forms.general.errors.maxDate', {
          max: moment(error.max).format(formatsCatalog.date.moment),
        }),
    };
    this.errors = Object.keys(this.messages);

    this.addSubscription(
      this.inputValuesChange.subscribe((inputValues) => {
        this.initFormArrays(inputValues);
      })
    );
  }

  private initFormArrays(values: any) {
    const sortedPaths = _.sortBy(Object.keys(this.formArrays), (path) => path.split('[?]').length);
    sortedPaths.forEach((path) => {
      const resolvedPaths = helpers.buildPath(values, path);
      resolvedPaths.forEach((resolvedPath) => {
        const newFormArray = _.get(values, resolvedPath, []);
        const oldFormArray = this.getFormArray(resolvedPath);
        const diff = _.get(newFormArray, 'length', 0) - _.get(oldFormArray, 'length', 0);

        if (diff > 0) _.times(diff, (i) => this.addFormArrayItem(resolvedPath, newFormArray[i]));
        else if (diff < 0) _.times(-diff, () => this.removeFormArrayItem(resolvedPath, 0));
      });
    });
  }

  private modifyInputValues(values: any) {
    let modifiedInputValues = values;

    this.inputValuesModifiers.forEach((modifier) => {
      modifiedInputValues = modifier(modifiedInputValues);
    });

    return modifiedInputValues;
  }

  private modifyOutputValues(values: any) {
    let modifiedOutputValues = values;

    this.outputValuesModifiers.forEach((modifier) => {
      modifiedOutputValues = modifier(modifiedOutputValues);
    });

    return modifiedOutputValues;
  }

  ngAfterViewInit(): void {
    this.formGroup.patchValue(this.inputValues);
    this.changeDetection.detectChanges();
  }

  override ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);

    if (changes['values']) this.formGroup.patchValue(this.inputValues);
  }

  getResetValues(): any {
    return {};
  }

  getValues(): any {
    return this.modifyOutputValues(_.cloneDeep(this.values));
  }

  resetForm() {
    this.values = this.getResetValues();
    this.form.resetForm(this.values);
  }

  registerInputValuesModifier(modifier: (values: any) => any) {
    this.inputValuesModifiers.push(modifier);
  }

  registerOutputValuesModifier(modifier: (values: any) => any) {
    this.outputValuesModifiers.push(modifier);
  }

  registerError(name: string, translation: (error: any) => string) {
    this.messages[name] = translation;
    this.errors = Object.keys(this.messages);
  }

  registerFormArray(path: string, itemConstructor: (values?: any) => AbstractControl) {
    this.formArrays[path] = { itemConstructor };
  }

  getError = (name: string, formGroup: UntypedFormGroup = null) => {
    let currentGroup = formGroup || this.formGroup;
    const paths = name.split('.');
    const pathCount = paths.length - 1;

    if (pathCount > 0) {
      for (let i = 0; i < pathCount; i++) {
        currentGroup = currentGroup.get(paths[i]) as UntypedFormGroup;
      }
    }

    const control = currentGroup.get(paths[pathCount]); // last path is a name of a control

    return this.getFormControlError(control);
  };

  getFormControlError = (formControl: AbstractControl) => {
    if (formControl && formControl.errors) {
      for (const [key, error] of Object.entries(formControl.errors)) {
        const message = this.messages[_.camelCase(key)];
        if (message) return message(error);
        console.warn(`No message exist for ${key} error: `, error);
      }
    }
    return null;
  };

  getFormArray = (path: string): UntypedFormArray => {
    return this.formGroup.get(helpers.normalizePath(path)) as UntypedFormArray;
  };

  clearFormArray = (path: string) => {
    const formArray = this.getFormArray(path);
    formArray.clear();
  };

  getFormArrayGroups = (path: string): Array<UntypedFormGroup> => {
    const formArray = this.getFormArray(path);
    if (!formArray) return [];
    return formArray.controls.map((control) => <UntypedFormGroup>control);
  };

  insertFormArrayItem = (path: string, index: number, values?: any) => {
    const formArray = this.getFormArray(path);
    const formArrayDesc = Object.entries(this.formArrays).find(([key]) => helpers.generalizePath(key) === helpers.generalizePath(path));

    formArray.insert(index, formArrayDesc[1].itemConstructor(values));
  };

  addFormArrayItem = (path: string, values?: any) => {
    const formArray = this.getFormArray(path);

    this.insertFormArrayItem(path, formArray.length, values);
  };

  findFormArrayItem = (path: string, predicate: (control: AbstractControl, index: number) => boolean): AbstractControl => {
    const formArray = this.getFormArray(path);

    for (let i = 0; i < formArray.length; i++) {
      const item = formArray.at(i);
      if (predicate(item, i)) return item;
    }

    return null;
  };

  findFormArrayItems = (path: string, predicate: (control: AbstractControl, index: number) => boolean): Array<AbstractControl> => {
    const controls = [];
    const formArray = this.getFormArray(path);

    for (let i = 0; i < formArray.length; i++) {
      const item = formArray.at(i);
      if (predicate(item, i)) controls.push(item);
    }

    return controls;
  };

  removeFormArrayItem = (path: string, index: number) => {
    const formArray = this.getFormArray(path);
    formArray.removeAt(index);
  };

  moveFormArrayItem = (path: string, oldIndex: number, newIndex: number) => {
    const formArray = this.getFormArray(path);
    if (newIndex < 0) return;
    if (newIndex >= formArray.length) return;

    const control = formArray.at(oldIndex);
    if (!control) return;

    formArray.removeAt(oldIndex);
    formArray.insert(newIndex, control);
  };

  isReadonly = (name: string) => {
    return this.readonly.find((item) => item === name || item === '#everything#') ? 'readonly' : null;
  };

  isHidden = (name: string) => {
    return Boolean(this.hidden.find((item) => item === name));
  };

  isValid() {
    return this.form.valid;
  }

  onSubmit() {
    if (this.formGroup) this.formGroup.markAllAsTouched();
    if (this.isValid()) this.handleSubmit.emit({ values: this.getValues(), formLoading: this.formLoading });
    return false;
  }

  submit() {
    this.form.ngSubmit.emit();
    (<any>this.form).submitted = true;
    this.form.form.markAsPristine();
  }
}

export class FormLoading {
  public loading: boolean;

  constructor() {
    this.loading = false;
  }

  start() {
    this.loading = true;
  }

  stop() {
    this.loading = false;
  }
}
